import React, { useState } from 'react';
import {
    Modal,
    ModalBody,
    ModalCloseBtn,
    ModalContent,
    ModalFooter,
    ModalHeader,
    useModalContext,
} from '@fiverr-private/overlay';
import { Button } from '@fiverr-private/button';
import { I18n } from '@fiverr-private/i18n-react';
import { logger } from '@fiverr-private/obs';

export interface ConfirmModalProps {
    title: string;
    description: string;
    confirmText: string;
    onConfirm: () => void | Promise<void>;
    isOpen: boolean;
    onClose: () => void;
}

const TRANSLATION_PREFIX = 'legal_documents_package.confirm_modal';

const ConfirmModalContent = ({
    description,
    confirmText,
    onConfirm,
    onClose,
}: Omit<ConfirmModalProps, 'isOpen' | 'title'>) => {
    const [isSubmitting, setSubmitting] = useState(false);
    const { close } = useModalContext();

    const handleClose = () => {
        onClose();
        close();
    };

    const handleConfirm = async () => {
        try {
            setSubmitting(true);
            await onConfirm();
            handleClose();
        } catch (error) {
            logger.error(error as Error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <ModalBody>{description}</ModalBody>
            <ModalFooter>
                <Button onClick={handleClose} variant="outline">
                    <I18n k={`${TRANSLATION_PREFIX}.cancel`} />
                </Button>
                <Button onClick={handleConfirm} disabled={isSubmitting} loading={isSubmitting} colorScheme="red">
                    {confirmText}
                </Button>
            </ModalFooter>
        </>
    );
};

export const ConfirmModal = ({ title, isOpen, ...props }: ConfirmModalProps) => (
    <Modal isOpen={isOpen} onClose={props.onClose}>
        <ModalContent size="sm">
            <ModalHeader>
                {title}
                <ModalCloseBtn />
            </ModalHeader>

            <ConfirmModalContent {...props} />
        </ModalContent>
    </Modal>
);
